import * as ROUTES from './routes';
import * as SURVEYS from './survey-ids';

const RECORDS_TYPES = {
  appointmentHistory: 'Appointment History',
  surgeries: 'Surgeries',
  emergencyVisits: 'Emergency Visits',
  hospitalizations: 'Hospitalizations',
  medicalConditions: 'Medical Conditions',
  vitalSigns: 'Vital Signs',
  medications: 'Medications',
  geneticInformation: 'Genetic Information',
  allergies: 'Allergies',
  immunizations: 'Immunizations',
  imagingTests: 'Imaging and Tests',
  labResults: 'Lab Results',
  familyHistory: 'Family History',
  contacts: 'Contacts',
  insurance: 'Insurance',
  fitness: 'Daily Stats',
  body: 'Body',
  sleep: 'Sleep',
  activity: 'Activity',
  bodyScans: 'Body Scan History',
  faceScans: 'Face Scan History',
  coughScans: 'Cough Scan History',
  skinScans: 'Skin Scan History',
} as const;

const VITAL_SIGN_TYPES = {
  pain: 'Pain',
  oxygenSaturation: 'Oxygen Saturation',
  bloodPressure: 'Blood Pressure',
  respiration: 'Respiratory Rate',
  heartRate: 'Heart Rate',
  bloodSugar: 'Blood Sugar',
  temperature: 'Temperature',
} as const;

export const RECORD_URLS = {
  [SURVEYS.ACTIVITY_ID]: ROUTES.ACTIVITY,
  [SURVEYS.ALLERGY_ID]: ROUTES.ALLERGIES,
  [SURVEYS.APPOINTMENTS_ID]: ROUTES.APPOINTMENT_HISTORY,
  [SURVEYS.BODY_ID]: ROUTES.BODY,
  [SURVEYS.BODY_SCAN_ID]: ROUTES.BODY_SCAN,
  [SURVEYS.CONTACT_ID]: ROUTES.CONTACTS,
  [SURVEYS.COUGH_SCAN_ID]: ROUTES.COUGH_SCAN,
  [SURVEYS.EMERGENCY_VISIT_ID]: ROUTES.EMERGENCY_VISITS,
  [SURVEYS.FACE_SCAN_ID]: ROUTES.FACE_SCAN,
  [SURVEYS.FAMILY_HISTORY_ID]: ROUTES.FAMILY_HISTORY,
  [SURVEYS.FITNESS_ID]: ROUTES.FITNESS,
  [SURVEYS.CARRIER_STATUS_ID]: ROUTES.GENETIC_INFORMATION,
  [SURVEYS.HEALTH_RISK_ID]: ROUTES.GENETIC_INFORMATION,
  [SURVEYS.HOSPITALIZATION_ID]: ROUTES.HOSPITALIZATIONS,
  [SURVEYS.IMAGING_TESTS_ID]: ROUTES.IMAGING_TESTS,
  [SURVEYS.IMMUNIZATION_ID]: ROUTES.IMMUNIZATIONS,
  [SURVEYS.LAB_RESULTS_ID]: ROUTES.LAB_RESULTS,
  [SURVEYS.MEDICAL_CONDITION_ID]: ROUTES.MEDICAL_CONDITIONS,
  [SURVEYS.MEDICATION_ID]: ROUTES.MEDICATIONS,
  [SURVEYS.SKIN_SCAN_ID]: ROUTES.SKIN_SCAN,
  [SURVEYS.SLEEP_ID]: ROUTES.SLEEP,
  [SURVEYS.SURGERY_ID]: ROUTES.SURGERIES,
  [SURVEYS.VITAL_SIGN_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_OXYGEN_SATURATION_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_TEMPERATURE_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_PAIN_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_RESPIRATION_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_BLOOD_PRESSURE_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_HEART_RATE_ID]: ROUTES.VITAL_SIGNS,
  [SURVEYS.VITAL_SIGN_BLOOD_SUGAR_ID]: ROUTES.VITAL_SIGNS,
} as const;

export const ADD_RECORD_URLS = {
  [SURVEYS.ALLERGY_ID]: `${ROUTES.ALLERGIES}add`,
  [SURVEYS.APPOINTMENTS_ID]: `${ROUTES.APPOINTMENT_HISTORY}add`,
  [SURVEYS.BODY_ID]: `${ROUTES.BODY}add`,
  [SURVEYS.EMERGENCY_VISIT_ID]: `${ROUTES.EMERGENCY_VISITS}add`,
  [SURVEYS.FAMILY_HISTORY_ID]: `${ROUTES.FAMILY_HISTORY}add`,
  [SURVEYS.CARRIER_STATUS_ID]: `${ROUTES.GENETIC_INFORMATION}add`,
  [SURVEYS.HEALTH_RISK_ID]: `${ROUTES.GENETIC_INFORMATION}add`,
  [SURVEYS.HOSPITALIZATION_ID]: `${ROUTES.HOSPITALIZATIONS}add`,
  [SURVEYS.IMAGING_TESTS_ID]: `${ROUTES.IMAGING_TESTS}add`,
  [SURVEYS.IMMUNIZATION_ID]: `${ROUTES.IMMUNIZATIONS}add`,
  [SURVEYS.LAB_RESULTS_ID]: `${ROUTES.LAB_RESULTS}add`,
  [SURVEYS.MEDICAL_CONDITION_ID]: `${ROUTES.MEDICAL_CONDITIONS}add`,
  [SURVEYS.MEDICATION_ID]: `${ROUTES.MEDICATIONS}add`,
  [SURVEYS.SURGERY_ID]: `${ROUTES.SURGERIES}add`,
  [SURVEYS.VITAL_SIGN_ID]: `${ROUTES.VITAL_SIGNS}add`,
  [SURVEYS.INSURANCE_PROGRAMS]: `${ROUTES.INSURANCE}add`,
} as const;

export const RECORD_NAMES = {
  [SURVEYS.ALLERGY_ID]: RECORDS_TYPES.allergies,
  [SURVEYS.APPOINTMENTS_ID]: RECORDS_TYPES.appointmentHistory,
  [SURVEYS.BODY_SCAN_ID]: RECORDS_TYPES.bodyScans,
  [SURVEYS.CONTACT_ID]: RECORDS_TYPES.contacts,
  [SURVEYS.COUGH_SCAN_ID]: RECORDS_TYPES.coughScans,
  [SURVEYS.EMERGENCY_VISIT_ID]: RECORDS_TYPES.emergencyVisits,
  [SURVEYS.FACE_SCAN_ID]: RECORDS_TYPES.faceScans,
  [SURVEYS.FAMILY_HISTORY_ID]: RECORDS_TYPES.familyHistory,
  [SURVEYS.CARRIER_STATUS_ID]: RECORDS_TYPES.geneticInformation,
  [SURVEYS.HEALTH_RISK_ID]: RECORDS_TYPES.geneticInformation,
  [SURVEYS.HOSPITALIZATION_ID]: RECORDS_TYPES.hospitalizations,
  [SURVEYS.IMAGING_TESTS_ID]: RECORDS_TYPES.imagingTests,
  [SURVEYS.IMMUNIZATION_ID]: RECORDS_TYPES.immunizations,
  [SURVEYS.LAB_RESULTS_ID]: RECORDS_TYPES.labResults,
  [SURVEYS.MEDICAL_CONDITION_ID]: RECORDS_TYPES.medicalConditions,
  [SURVEYS.MEDICATION_ID]: RECORDS_TYPES.medications,
  [SURVEYS.SKIN_SCAN_ID]: RECORDS_TYPES.skinScans,
  [SURVEYS.SURGERY_ID]: RECORDS_TYPES.surgeries,
  [SURVEYS.VITAL_SIGN_ID]: RECORDS_TYPES.vitalSigns,
  [SURVEYS.VITAL_SIGN_OXYGEN_SATURATION_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.oxygenSaturation}`,
  [SURVEYS.VITAL_SIGN_TEMPERATURE_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.temperature}`,
  [SURVEYS.VITAL_SIGN_PAIN_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.pain}`,
  [SURVEYS.VITAL_SIGN_RESPIRATION_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.respiration}`,
  [SURVEYS.VITAL_SIGN_BLOOD_PRESSURE_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.bloodPressure}`,
  [SURVEYS.VITAL_SIGN_HEART_RATE_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.heartRate}`,
  [SURVEYS.VITAL_SIGN_BLOOD_SUGAR_ID]: `${RECORDS_TYPES.vitalSigns} - ${VITAL_SIGN_TYPES.bloodSugar}`,
  [SURVEYS.INSURANCE_PROGRAMS]: RECORDS_TYPES.insurance,
  [SURVEYS.FITNESS_ID]: RECORDS_TYPES.fitness,
  [SURVEYS.BODY_ID]: RECORDS_TYPES.body,
  [SURVEYS.SLEEP_ID]: RECORDS_TYPES.sleep,
  [SURVEYS.ACTIVITY_ID]: RECORDS_TYPES.activity,
} as const;
